import { useLayoutEffect, useState, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { GameProviderMainMenu } from "../GameProviderMainMenu";
import { SCREENS } from "@educaplay/store/constants";
import { setView, startGame } from "@educaplay/store/slices/gameSlice";
import { GameProviderContent } from "../GameProviderContent";
import { GameProviderScore } from "../GameProviderScore";
import styles from './GameproviderView.module.scss';
import PropTypes from 'prop-types';
import GameproviderViewTutorial from "./GameproviderViewTutorial";
import clsx from "clsx";

export function GameProviderView(props) {
    const { gameImage, gameTutorial, Game, hasCustomLayout, preloadDuringCountdown = false, classes: customClasses } = props

    const [showScoreScreen, setShowScoreScreen] = useState(false);
    const [isCountdownActive, setIsCountdownActive] = useState(false);
    const dispatch = useDispatch();
    const screen = useSelector(state => state.game.view);
    const backgroundImage = useSelector(state => state.settings.customizations.backgroundImage);
    const gameContentRef = useRef();
    const mainMenuRef = useRef();
    const [showTutorial, setShowTutorial] = useState(gameTutorial?.image !== undefined);
    useLayoutEffect(() => {
        setShowScoreScreen(false);
        if (screen === SCREENS.SCORE_SCREEN) {
            gameContentRef.current.animateExit().then(() => {
                setShowScoreScreen(true);
            })
        }
    }, [screen])

    const handleStartGame = useCallback(() => {
        dispatch(startGame());
        // Entry animations will run via GameProviderContent's useEffect
        // when screen changes to GAME
        setIsCountdownActive(false);
    }, [dispatch])

    const handleCountdownStart = useCallback(() => {
        if (preloadDuringCountdown) {
            // Small delay so the countdown animation starts smoothly
            // before mounting the heavy game content (Phaser)
            setTimeout(() => {
                setIsCountdownActive(true);
            }, 300);
        }
    }, [preloadDuringCountdown])

    const handleRestartGame = () => {
        setIsCountdownActive(false);
        dispatch(setView(SCREENS.MAIN_MENU));
        setTimeout(() => {
            mainMenuRef.current?.start();
        }, 400)
    }

    // Render game content when countdown is active (preloading) or when past MAIN_MENU
    const showGameContent = isCountdownActive || screen !== SCREENS.MAIN_MENU;
    const showMainMenu = screen === SCREENS.MAIN_MENU;
    const showBackgroundImage = backgroundImage.showGame && backgroundImage.src;

    return (
        <>
            {showGameContent && (
                <div key="game-content" className={clsx(styles.gameScreen, customClasses.screen)} style={showBackgroundImage ? { backgroundImage: `url(${backgroundImage.src})` } : undefined}>
                    {showBackgroundImage && (<div className={styles.gameScreenShadow} />)}

                    <div className={styles.container}>
                        <GameProviderContent ref={gameContentRef} Game={Game} hasCustomLayout={hasCustomLayout} classes={customClasses} />
                    </div>
                    <div id="swipeable-drawer-portal-element"/>
                </div>
            )}

            {showMainMenu && (
                <div key="main-menu" className={isCountdownActive ? styles.mainMenuOverlay : styles.mainMenuWrapper}>
                    <GameProviderMainMenu
                        gameImage={gameImage}
                        ref={mainMenuRef}
                        onStart={handleStartGame}
                        onCountdownStart={handleCountdownStart}
                    />
                </div>
            )}

            {showTutorial && (
                <GameproviderViewTutorial
                    imageSource={gameTutorial.image}
                    onClose={() => setShowTutorial(false)}
                    delay={gameTutorial.delay}
                    duration={gameTutorial.duration}
                />
            )}

            {showScoreScreen && (
                <GameProviderScore onRestart={handleRestartGame} />
            )}
        </>
    );
}


GameProviderView.propTypes = {
    gameImage: PropTypes.string.isRequired,
    Game: PropTypes.any.isRequired,
    hasCustomLayout: PropTypes.bool,
    preloadDuringCountdown: PropTypes.bool,
    gameTutorial: PropTypes.shape({
        image: PropTypes.string.isRequired,
        duration: PropTypes.number.isRequired
    }),
    classes: PropTypes.object
}
